import { Song, Setlist } from '../types/song';
import { getFeatureFlagStatus } from '../config/featureFlags';
import { config } from '../config/environment';
import { retryWithBackoff } from '../utils/retryWithBackoff';

/**
 * Custom error types for storage operations
 */
export class StorageError extends Error {
  constructor(message: string, public code: string) {
    super(message);
    this.name = 'StorageError';
  }
}

export class NotFoundError extends StorageError {
  constructor(resource: string, id: string) {
    super(`${resource} with id ${id} not found`, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class StorageQuotaExceededError extends StorageError {
  constructor() {
    super('Storage quota exceeded', 'QUOTA_EXCEEDED');
    this.name = 'StorageQuotaExceededError';
  }
}

export class ConflictError extends StorageError {
  constructor(message: string) {
    super(message, 'CONFLICT');
    this.name = 'ConflictError';
  }
}

/**
 * Storage service interface for abstracting data persistence.
 * This allows us to swap between localStorage and API implementations.
 */
export interface StorageService {
  // Song operations
  getSongs(): Promise<Song[]>;
  getSong(id: string): Promise<Song | null>;
  saveSong(song: Song): Promise<void>;
  updateSong(id: string, updates: Partial<Song>): Promise<void>;
  deleteSong(id: string): Promise<void>;
  
  // Setlist operations
  getSetlists(): Promise<Setlist[]>;
  getSetlist(id: string): Promise<Setlist | null>;
  saveSetlist(setlist: Setlist): Promise<void>;
  updateSetlist(id: string, updates: Partial<Setlist>): Promise<void>;
  deleteSetlist(id: string): Promise<void>;
  
  // Bulk operations
  exportData(): Promise<{ songs: Song[]; setlists: Setlist[]; version: string }>;
  importData(data: { songs: Song[]; setlists: Setlist[] }): Promise<void>;
}

/**
 * Operation queue to prevent race conditions
 */
class OperationQueue {
  private queue: Array<() => Promise<any>> = [];
  private processing = false;

  async enqueue<T>(operation: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          const result = await operation();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      });
      this.process();
    });
  }

  private async process() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;
    
    while (this.queue.length > 0) {
      const operation = this.queue.shift();
      if (operation) {
        await operation();
      }
    }
    
    this.processing = false;
  }
}

/**
 * Local storage implementation of the StorageService.
 * This is our current implementation that directly uses browser localStorage.
 */
export class LocalStorageService implements StorageService {
  private operationQueue = new OperationQueue();
  private readonly STORAGE_KEY = 'commonchord-songs';
  private readonly STORAGE_VERSION = '1.0';

  private async getStorageData(): Promise<{ songs: Song[]; setlists: Setlist[] }> {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) {
        return { songs: [], setlists: [] };
      }
      const parsed = JSON.parse(data);
      return {
        songs: parsed.songs || [],
        setlists: parsed.setlists || []
      };
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      if (error instanceof SyntaxError) {
        throw new StorageError('Corrupted storage data', 'PARSE_ERROR');
      }
      return { songs: [], setlists: [] };
    }
  }

  private async setStorageData(data: { songs: Song[]; setlists: Setlist[] }): Promise<void> {
    try {
      const serialized = JSON.stringify(data);
      localStorage.setItem(this.STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Error writing to localStorage:', error);
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        throw new StorageQuotaExceededError();
      }
      throw new StorageError('Failed to save data to storage', 'WRITE_ERROR');
    }
  }

  // Song operations
  async getSongs(): Promise<Song[]> {
    return this.operationQueue.enqueue(async () => {
      const { songs } = await this.getStorageData();
      return songs;
    });
  }

  async getSong(id: string): Promise<Song | null> {
    return this.operationQueue.enqueue(async () => {
      const { songs } = await this.getStorageData();
      return songs.find(song => song.id === id) || null;
    });
  }

  async saveSong(song: Song): Promise<void> {
    return this.operationQueue.enqueue(async () => {
      const data = await this.getStorageData();
      // Check for duplicate ID
      if (data.songs.some(s => s.id === song.id)) {
        throw new ConflictError(`Song with id ${song.id} already exists`);
      }
      data.songs.push(song);
      await this.setStorageData(data);
    });
  }

  async updateSong(id: string, updates: Partial<Song>): Promise<void> {
    return this.operationQueue.enqueue(async () => {
      const data = await this.getStorageData();
      const index = data.songs.findIndex(song => song.id === id);
      if (index === -1) {
        throw new NotFoundError('Song', id);
      }
      data.songs[index] = {
        ...data.songs[index],
        ...updates,
        lastModified: new Date().toISOString()
      };
      await this.setStorageData(data);
    });
  }

  async deleteSong(id: string): Promise<void> {
    return this.operationQueue.enqueue(async () => {
      const data = await this.getStorageData();
      const songExists = data.songs.some(song => song.id === id);
      if (!songExists) {
        throw new NotFoundError('Song', id);
      }
      data.songs = data.songs.filter(song => song.id !== id);
      // Also remove from setlists
      data.setlists = data.setlists.map(setlist => ({
        ...setlist,
        songs: setlist.songs.filter(songId => songId !== id)
      }));
      await this.setStorageData(data);
    });
  }

  // Setlist operations
  async getSetlists(): Promise<Setlist[]> {
    return this.operationQueue.enqueue(async () => {
      const { setlists } = await this.getStorageData();
      return setlists;
    });
  }

  async getSetlist(id: string): Promise<Setlist | null> {
    return this.operationQueue.enqueue(async () => {
      const { setlists } = await this.getStorageData();
      return setlists.find(setlist => setlist.id === id) || null;
    });
  }

  async saveSetlist(setlist: Setlist): Promise<void> {
    return this.operationQueue.enqueue(async () => {
      const data = await this.getStorageData();
      // Check for duplicate ID
      if (data.setlists.some(s => s.id === setlist.id)) {
        throw new ConflictError(`Setlist with id ${setlist.id} already exists`);
      }
      data.setlists.push(setlist);
      await this.setStorageData(data);
    });
  }

  async updateSetlist(id: string, updates: Partial<Setlist>): Promise<void> {
    return this.operationQueue.enqueue(async () => {
      const data = await this.getStorageData();
      const index = data.setlists.findIndex(setlist => setlist.id === id);
      if (index === -1) {
        throw new NotFoundError('Setlist', id);
      }
      data.setlists[index] = {
        ...data.setlists[index],
        ...updates,
        lastModified: new Date().toISOString()
      };
      await this.setStorageData(data);
    });
  }

  async deleteSetlist(id: string): Promise<void> {
    return this.operationQueue.enqueue(async () => {
      const data = await this.getStorageData();
      const setlistExists = data.setlists.some(setlist => setlist.id === id);
      if (!setlistExists) {
        throw new NotFoundError('Setlist', id);
      }
      data.setlists = data.setlists.filter(setlist => setlist.id !== id);
      await this.setStorageData(data);
    });
  }

  // Bulk operations
  async exportData(): Promise<{ songs: Song[]; setlists: Setlist[]; version: string }> {
    return this.operationQueue.enqueue(async () => {
      const data = await this.getStorageData();
      return {
        ...data,
        version: this.STORAGE_VERSION
      };
    });
  }

  async importData(importedData: { songs: Song[]; setlists: Setlist[] }): Promise<void> {
    return this.operationQueue.enqueue(async () => {
      // Validate imported data
      if (!importedData || typeof importedData !== 'object') {
        throw new StorageError('Invalid import data', 'INVALID_DATA');
      }
      
      if (!Array.isArray(importedData.songs) || !Array.isArray(importedData.setlists)) {
        throw new StorageError('Import data must contain songs and setlists arrays', 'INVALID_DATA');
      }
      
      const currentData = await this.getStorageData();
      // Merge imported data with existing data, avoiding duplicates by ID
      const existingSongIds = new Set(currentData.songs.map(s => s.id));
      const existingSetlistIds = new Set(currentData.setlists.map(s => s.id));
      
      const mergedData = {
        songs: [
          ...currentData.songs,
          ...importedData.songs.filter(s => !existingSongIds.has(s.id))
        ],
        setlists: [
          ...currentData.setlists,
          ...importedData.setlists.filter(s => !existingSetlistIds.has(s.id))
        ]
      };
      
      await this.setStorageData(mergedData);
    });
  }
}

/**
 * API implementation of the StorageService.
 * Communicates with the backend song-service Lambda.
 */
export class APIStorageService implements StorageService {
  private apiUrl: string;
  private headers = {
    'Content-Type': 'application/json'
  };

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      
      if (response.status === 404) {
        throw new NotFoundError('Resource', 'not found');
      } else if (response.status === 400) {
        throw new StorageError(error.error || 'Bad request', 'BAD_REQUEST');
      } else if (response.status === 500) {
        throw new StorageError(error.error || 'Server error', 'SERVER_ERROR');
      }
      
      throw new StorageError(error.error || `HTTP ${response.status}`, `HTTP_${response.status}`);
    }
    
    return response.json();
  }

  async getSongs(): Promise<Song[]> {
    return retryWithBackoff(async () => {
      const response = await fetch(`${this.apiUrl}/songs`, {
        method: 'GET',
        headers: this.headers
      });
      
      const data = await this.handleResponse<{ songs: Song[]; count: number }>(response);
      return data.songs;
    });
  }

  async getSong(id: string): Promise<Song | null> {
    try {
      const response = await fetch(`${this.apiUrl}/songs/${id}`, {
        method: 'GET',
        headers: this.headers
      });
      
      return await this.handleResponse<Song>(response);
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }
      throw error;
    }
  }

  async saveSong(song: Song): Promise<void> {
    await retryWithBackoff(async () => {
      const response = await fetch(`${this.apiUrl}/songs`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify(song)
      });
      
      await this.handleResponse<Song>(response);
    });
  }

  async updateSong(id: string, updates: Partial<Song>): Promise<void> {
    console.log('APIStorageService.updateSong:', { id, updates, url: `${this.apiUrl}/songs/${id}` });
    
    await retryWithBackoff(async () => {
      const response = await fetch(`${this.apiUrl}/songs/${id}`, {
        method: 'PUT',
        headers: this.headers,
        body: JSON.stringify(updates)
      });
      
      console.log('Update response:', { status: response.status, ok: response.ok });
      
      if (!response.ok) {
        const errorBody = await response.text();
        console.error('Update failed:', { status: response.status, body: errorBody });
      }
      
      await this.handleResponse<Song>(response);
    });
  }

  async deleteSong(id: string): Promise<void> {
    const response = await fetch(`${this.apiUrl}/songs/${id}`, {
      method: 'DELETE',
      headers: this.headers
    });
    
    await this.handleResponse<{ message: string }>(response);
  }

  // Setlist operations - not implemented in backend yet
  async getSetlists(): Promise<Setlist[]> {
    // For now, return empty array until setlist endpoints are implemented
    return [];
  }

  async getSetlist(id: string): Promise<Setlist | null> {
    // For now, return null until setlist endpoints are implemented
    return null;
  }

  async saveSetlist(setlist: Setlist): Promise<void> {
    // For now, throw error until setlist endpoints are implemented
    throw new StorageError('Setlist API not implemented yet', 'NOT_IMPLEMENTED');
  }

  async updateSetlist(id: string, updates: Partial<Setlist>): Promise<void> {
    // For now, throw error until setlist endpoints are implemented
    throw new StorageError('Setlist API not implemented yet', 'NOT_IMPLEMENTED');
  }

  async deleteSetlist(id: string): Promise<void> {
    // For now, throw error until setlist endpoints are implemented
    throw new StorageError('Setlist API not implemented yet', 'NOT_IMPLEMENTED');
  }

  async exportData(): Promise<{ songs: Song[]; setlists: Setlist[]; version: string }> {
    // Get all songs from API
    const songs = await this.getSongs();
    
    return {
      songs,
      setlists: [], // Not implemented yet
      version: '1.0'
    };
  }

  async importData(data: { songs: Song[]; setlists: Setlist[] }): Promise<void> {
    // Import songs one by one (or batch if backend supports it)
    for (const song of data.songs) {
      try {
        await this.saveSong(song);
      } catch (error) {
        // Skip duplicates, continue with others
        if (error instanceof ConflictError) {
          console.warn(`Song ${song.id} already exists, skipping`);
          continue;
        }
        throw error;
      }
    }
    
    // Setlists not implemented yet
    if (data.setlists && data.setlists.length > 0) {
      console.warn('Setlist import not implemented yet');
    }
  }
}

/**
 * Storage service factory.
 * Returns the appropriate storage implementation based on feature flags.
 * Note: We don't cache the instance to ensure feature flag changes are respected.
 */
export function getStorageService(): StorageService {
  const useServerPersistence = getFeatureFlagStatus('USE_SERVER_PERSISTENCE');
  
  if (useServerPersistence && config.apiUrl) {
    // Use API storage when feature is enabled and API is available
    // In development, still use API if it's configured (even if localhost)
    return new APIStorageService(config.apiUrl);
  } else {
    // Fall back to localStorage when API is not configured or feature is disabled
    return new LocalStorageService();
  }
}

// For testing purposes - create singleton instances when needed
let testLocalStorage: LocalStorageService | null = null;
let testApiStorage: APIStorageService | null = null;

export function getTestStorageService(forceLocal = false): StorageService {
  if (forceLocal || !config.apiUrl) {
    if (!testLocalStorage) {
      testLocalStorage = new LocalStorageService();
    }
    return testLocalStorage;
  }
  
  if (!testApiStorage) {
    testApiStorage = new APIStorageService(config.apiUrl);
  }
  return testApiStorage;
}

// For testing purposes
export function resetStorageService(): void {
  testLocalStorage = null;
  testApiStorage = null;
}